import React, { useState, useEffect } from 'react'
import {
  Users,
  UserCheck,
  ClipboardList,
  Plus,
  Trash2,
  Edit2,
  CheckCircle,
  ExternalLink,
  FileText,
  Star,
  MessageSquare,
  Send,
  Loader2,
  AlertCircle,
  ChevronRight,
  BookOpen,
  Upload
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import DashboardSidebar from '../../features/lesson-builder/components/DashboardSidebar'
import {
  collaborationService,
  CollaborativeGroup,
  GroupTask,
  GroupSubmission,
  PeerReview,
  GroupMember
} from '../../services/collaboration/collaborationService'
import { lessonApi } from '../../features/lesson-builder/api/lessonBuilderApi'

// GV chấm điểm chính thức cho sản phẩm nhóm (thang /10) — #3
const GroupGradeBox: React.FC<{ sub: GroupSubmission; onGraded: (s: GroupSubmission) => void }> = ({ sub, onGraded }) => {
  const [score, setScore] = useState(sub.teacher_score != null ? String(sub.teacher_score) : '')
  const [fb, setFb] = useState(sub.teacher_feedback || '')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    const n = Number(score)
    if (isNaN(n) || n < 0 || n > 10) { alert('Điểm trong khoảng 0–10.'); return }
    setBusy(true)
    try { onGraded(await collaborationService.gradeSubmission({ submission_id: sub.id, score: n, feedback: fb })) }
    catch (e: any) { alert('Lỗi chấm điểm: ' + (e?.message || '')) }
    finally { setBusy(false) }
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input type="number" min={0} max={10} step={0.25} value={score} onChange={(e) => setScore(e.target.value)} placeholder="/10"
        className="w-20 p-2 border border-gray-200 rounded-lg text-sm text-center outline-none focus:ring-2 focus:ring-emerald-100" />
      <input value={fb} onChange={(e) => setFb(e.target.value)} placeholder="Nhận xét cho nhóm (tuỳ chọn)…"
        className="flex-1 min-w-[160px] p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-100" />
      <button onClick={save} disabled={busy} className="px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50">
        {busy ? '...' : (sub.teacher_score != null ? 'Cập nhật điểm' : 'Chấm điểm')}
      </button>
    </div>
  )
}

const CollaborationPage: React.FC = () => {
  const { user } = useAuth()
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin'

  const [activeTab, setActiveTab] = useState(isTeacher ? 'groups' : 'my-group')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Data states
  const [groups, setGroups] = useState<CollaborativeGroup[]>([])
  const [tasks, setTasks] = useState<GroupTask[]>([])
  const [submissions, setSubmissions] = useState<GroupSubmission[]>([])
  const [myGroups, setMyGroups] = useState<CollaborativeGroup[]>([])

  // Modal / Form states
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<CollaborativeGroup | null>(null)
  const [groupName, setGroupName] = useState('')
  const [className, setClassName] = useState('')
  const [groupMembers, setGroupMembers] = useState<{ student_email: string; student_name: string; role: string }[]>([
    { student_email: '', student_name: '', role: 'Leader' }
  ])

  const [showTaskModal, setShowTaskModal] = useState(false)
  const [editingTask, setEditingTask] = useState<GroupTask | null>(null)   // #4: sửa nhiệm vụ
  const [taskTitle, setTaskTitle] = useState('')
  const [taskDesc, setTaskDesc] = useState('')
  const [taskClass, setTaskClass] = useState('')
  const [taskLesson, setTaskLesson] = useState('')            // #3: gắn nhiệm vụ vào bài học
  const [taskDueDate, setTaskDueDate] = useState('')
  const [lessons, setLessons] = useState<{ id: string; title: string }[]>([])
  // #1: chọn thành viên từ danh sách lớp
  const [roster, setRoster] = useState<{ student_email: string; student_name: string; class_name: string }[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)

  const [selectedTask, setSelectedTask] = useState<GroupTask | null>(null)
  const [submissionContent, setSubmissionContent] = useState('')
  const [submissionUrl, setSubmissionUrl] = useState('')
  const [uploadingSub, setUploadingSub] = useState(false)

  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewSubmission, setReviewSubmission] = useState<GroupSubmission | null>(null)
  const [reviewScore, setReviewScore] = useState(10)
  const [reviewFeedback, setReviewFeedback] = useState('')
  const [currentReviews, setCurrentReviews] = useState<PeerReview[]>([])
  const [mySubmissionReviews, setMySubmissionReviews] = useState<PeerReview[]>([])
  const [reviewTargetSub, setReviewTargetSub] = useState<GroupSubmission | null>(null)   // #6: nhóm được phân chấm
  const [showEditForm, setShowEditForm] = useState(false)

  // ---------------- FETCH DATA ----------------

  const loadTeacherData = async () => {
    setLoading(true)
    try {
      const g = await collaborationService.listTeacherGroups()
      const t = await collaborationService.listTasks()
      setGroups(g)
      setTasks(t)
      try {
        const ls = await lessonApi.list()
        setLessons((ls || []).map((l: any) => ({ id: l.id, title: l.meta?.tieuDe || l.title || '(bài chưa đặt tên)' })))
      } catch { /* danh sách bài học không bắt buộc */ }
    } catch (err: any) {
      setError(err?.message || 'Lỗi tải dữ liệu giáo viên.')
    } finally {
      setLoading(false)
    }
  }

  const loadStudentData = async () => {
    setLoading(true)
    try {
      const myG = await collaborationService.listMyGroups()
      const t = await collaborationService.listTasks()
      setMyGroups(myG)
      setTasks(t)
    } catch (err: any) {
      setError(err?.message || 'Lỗi tải dữ liệu học sinh.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isTeacher) {
      loadTeacherData()
    } else {
      loadStudentData()
    }
  }, [isTeacher])

  // ---------------- TEACHER GROUP OPERATIONS ----------------

  const handleOpenGroupModal = (g: CollaborativeGroup | null = null) => {
    if (g) {
      setEditingGroup(g)
      setGroupName(g.group_name)
      setClassName(g.class_name || '')
      setGroupMembers(g.members.map(m => ({
        student_email: m.student_email,
        student_name: m.student_name || '',
        role: m.role
      })))
    } else {
      setEditingGroup(null)
      setGroupName('')
      setClassName('')
      setGroupMembers([{ student_email: '', student_name: '', role: 'Leader' }])
    }
    setShowGroupModal(true)
  }

  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!groupName || !className) {
      alert('Vui lòng điền tên nhóm và lớp.')
      return
    }
    const filteredMembers = groupMembers.filter(m => m.student_email.trim() !== '')
    if (filteredMembers.length === 0) {
      alert('Nhóm phải có ít nhất 1 thành viên.')
      return
    }

    try {
      if (editingGroup) {
        await collaborationService.updateGroup(editingGroup.id, {
          group_name: groupName,
          members: filteredMembers
        })
      } else {
        await collaborationService.createGroup({
          group_name: groupName,
          class_name: className,
          members: filteredMembers
        })
      }
      setShowGroupModal(false)
      loadTeacherData()
    } catch (err: any) {
      alert('Lỗi lưu nhóm: ' + err?.message)
    }
  }

  const handleDeleteGroup = async (id: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa nhóm này?')) return
    try {
      await collaborationService.deleteGroup(id)
      loadTeacherData()
    } catch (err: any) {
      alert('Lỗi xóa nhóm: ' + err?.message)
    }
  }

  // ---------------- TEACHER TASK OPERATIONS ----------------

  const handleOpenTaskModal = (t: GroupTask | null = null) => {
    setEditingTask(t)
    setTaskTitle(t?.title || '')
    setTaskDesc(t?.description || '')
    setTaskClass(t?.class_name || '')
    setTaskLesson(t?.lesson_id || '')
    setTaskDueDate(t?.due_date ? t.due_date.slice(0, 16) : '')
    setShowTaskModal(true)
  }

  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!taskTitle || !taskClass) {
      alert('Vui lòng nhập tiêu đề nhiệm vụ và lớp áp dụng.')
      return
    }
    try {
      const payload = { title: taskTitle, description: taskDesc, class_name: taskClass, lesson_id: taskLesson || undefined, due_date: taskDueDate || undefined }
      if (editingTask) await collaborationService.updateTask(editingTask.id, payload)
      else await collaborationService.createTask(payload)
      setShowTaskModal(false)
      setEditingTask(null)
      setTaskTitle(''); setTaskDesc(''); setTaskClass(''); setTaskLesson(''); setTaskDueDate('')
      loadTeacherData()
    } catch (err: any) {
      alert('Lỗi lưu nhiệm vụ: ' + err?.message)
    }
  }

  const handleDeleteTask = async (id: string) => {
    if (!confirm('Xoá nhiệm vụ này? (Sản phẩm nhóm đã nộp cũng sẽ không còn nhiệm vụ)')) return
    try {
      await collaborationService.deleteTask(id)
      if (selectedTask?.id === id) setSelectedTask(null)
      loadTeacherData()
    } catch (err: any) {
      alert('Lỗi xoá nhiệm vụ: ' + err?.message)
    }
  }

  // #1: tải danh sách lớp + chọn/bỏ thành viên
  const loadRoster = async () => {
    setRosterLoading(true)
    try {
      setRoster(await collaborationService.listRoster(className || undefined))
    } catch (err: any) {
      alert('Không tải được danh sách lớp: ' + (err?.message || '') + '\n(Thêm học sinh ở "Quản lý học sinh" trước.)')
    } finally {
      setRosterLoading(false)
    }
  }
  const toggleRosterMember = (r: { student_email: string; student_name: string }) => {
    setGroupMembers((prev) => {
      if (prev.some((m) => m.student_email === r.student_email)) {
        return prev.filter((m) => m.student_email !== r.student_email)
      }
      const cleaned = prev.filter((m) => m.student_email.trim() !== '')
      return [...cleaned, { student_email: r.student_email, student_name: r.student_name || '', role: 'Member' }]
    })
  }

  const handleSelectTask = async (t: GroupTask) => {
    setSelectedTask(t)
    setLoading(true)
    setShowEditForm(false)
    try {
      const subs = await collaborationService.getSubmissions(t.id)
      setSubmissions(subs)
      // #6: nhóm HS được PHÂN đánh giá chéo (round-robin) — chỉ cho HS
      if (!isTeacher) {
        collaborationService.reviewTarget(t.id).then(setReviewTargetSub).catch(() => setReviewTargetSub(null))
      }

      const myGroup = myGroups[0]
      if (myGroup) {
        const mySub = subs.find(s => s.group_id === myGroup.id)
        if (mySub) {
          const revs = await collaborationService.getReviews(mySub.id)
          setMySubmissionReviews(revs)
          setSubmissionContent(mySub.content || '')
          setSubmissionUrl(mySub.file_url || '')
        } else {
          setMySubmissionReviews([])
          setSubmissionContent('')
          setSubmissionUrl('')
        }
      }
    } catch (err: any) {
      alert('Lỗi tải danh sách bài nộp: ' + err?.message)
    } finally {
      setLoading(false)
    }
  }

  // ---------------- STUDENT SUBMISSION OPERATIONS ----------------

  const handleSubmitProduct = async (e: React.FormEvent, groupId: string) => {
    e.preventDefault()
    if (!selectedTask) return
    try {
      await collaborationService.submitWork({
        task_id: selectedTask.id,
        group_id: groupId,
        file_url: submissionUrl,
        content: submissionContent
      })
      alert('Đã nộp sản phẩm nhóm thành công!')
      setShowEditForm(false)
      // Refresh submissions and reviews list
      const subs = await collaborationService.getSubmissions(selectedTask.id)
      setSubmissions(subs)
      const mySub = subs.find(s => s.group_id === groupId)
      if (mySub) {
        const revs = await collaborationService.getReviews(mySub.id)
        setMySubmissionReviews(revs)
      }
    } catch (err: any) {
      alert('Lỗi nộp bài: ' + err?.message)
    }
  }

  // ---------------- PEER REVIEW OPERATIONS ----------------

  const handleOpenReviewModal = async (sub: GroupSubmission) => {
    setReviewSubmission(sub)
    setReviewScore(10)
    setReviewFeedback('')
    setShowReviewModal(true)
    try {
      const revs = await collaborationService.getReviews(sub.id)
      setCurrentReviews(revs)
    } catch (err: any) {
      console.error(err)
    }
  }

  const handleSaveReview = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reviewSubmission) return
    try {
      await collaborationService.submitReview({
        submission_id: reviewSubmission.id,
        score: reviewScore,
        feedback: reviewFeedback
      })
      alert('Gửi nhận xét thành công!')
      setShowReviewModal(false)
      if (selectedTask) {
        handleSelectTask(selectedTask)
      }
    } catch (err: any) {
      alert('Lỗi gửi nhận xét: ' + err?.message)
    }
  }

  return (
    <div className="flex bg-[#F8FAFC] min-h-screen">
      <DashboardSidebar />

      <main className="flex-1 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-blue-600" />
              Cộng tác học tập
            </h1>
            <p className="text-sm text-gray-500 mt-1">Làm việc nhóm, phân vai trò nhiệm vụ và đánh giá chéo sản phẩm học tập</p>
          </div>
          {isTeacher && (
            <div className="flex gap-2">
              <button
                onClick={() => handleOpenGroupModal(null)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 shadow-md transition"
              >
                <Plus size={18} /> Tạo nhóm mới
              </button>
              <button
                onClick={() => handleOpenTaskModal()}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-50 text-indigo-700 text-sm font-bold hover:bg-indigo-100 transition"
              >
                <ClipboardList size={18} /> Giao nhiệm vụ
              </button>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-2 border border-red-100">
            <AlertCircle size={18} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Loading spinner */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Tab Buttons */}
        <div className="flex border-b border-gray-200 mb-6">
          {isTeacher ? (
            <>
              <button
                onClick={() => { setActiveTab('groups'); setSelectedTask(null); }}
                className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                  activeTab === 'groups' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Danh sách nhóm
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                  activeTab === 'tasks' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Nhiệm vụ & Bài nộp
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setActiveTab('my-group')}
                className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                  activeTab === 'my-group' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Nhóm của tôi
              </button>
              <button
                onClick={() => setActiveTab('student-tasks')}
                className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                  activeTab === 'student-tasks' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Nhiệm vụ học tập
              </button>
            </>
          )}
        </div>

        {/* ---------------- TEACHER: GROUPS LIST TAB ---------------- */}
        {isTeacher && activeTab === 'groups' && !loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-400 bg-white rounded-2xl border border-dashed border-gray-200">
                <Users size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="font-semibold">Chưa có nhóm cộng tác nào</p>
                <p className="text-xs text-gray-400 mt-1">Bấm "Tạo nhóm mới" để phân chia lớp làm việc nhóm</p>
              </div>
            ) : (
              groups.map(g => (
                <div key={g.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col justify-between hover:shadow-md transition">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="bg-blue-50 text-blue-700 text-xs font-black px-2.5 py-1 rounded-full uppercase">
                        Lớp {g.class_name || 'Tự do'}
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => handleOpenGroupModal(g)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteGroup(g.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50 transition">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{g.group_name}</h3>
                    <div className="space-y-2 mb-4">
                      {g.members.map(m => (
                        <div key={m.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                          <span className="font-semibold text-gray-700">{m.student_name || m.student_email}</span>
                          <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">{m.role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">Đã tạo ngày: {new Date(g.created_at).toLocaleDateString('vi-VN')}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* ---------------- TEACHER: TASKS TAB ---------------- */}
        {isTeacher && activeTab === 'tasks' && !loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tasks List */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Danh sách nhiệm vụ</h2>
              {tasks.length === 0 ? (
                <div className="p-6 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
                  Chưa giao nhiệm vụ nhóm nào.
                </div>
              ) : (
                tasks.map(t => (
                  <div
                    key={t.id}
                    onClick={() => handleSelectTask(t)}
                    className={`p-5 rounded-2xl border cursor-pointer transition ${
                      selectedTask?.id === t.id
                        ? 'border-blue-600 bg-blue-50/30'
                        : 'border-gray-100 bg-white hover:bg-gray-50/55'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="bg-indigo-50 text-indigo-700 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">
                        Lớp {t.class_name}
                      </span>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleOpenTaskModal(t)} title="Sửa nhiệm vụ" className="p-1 text-gray-400 hover:text-blue-600"><Edit2 size={13} /></button>
                        <button onClick={() => handleDeleteTask(t.id)} title="Xoá nhiệm vụ" className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </div>
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm line-clamp-1">{t.title}</h4>
                    <p className="text-xs text-gray-400 mt-2">
                      Hạn: {t.due_date ? new Date(t.due_date).toLocaleDateString('vi-VN') : 'Không hạn'}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Submissions Detail */}
            <div className="lg:col-span-2">
              {selectedTask ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <h3 className="text-lg font-black text-gray-900 mb-2">{selectedTask.title}</h3>
                  <p className="text-sm text-gray-500 mb-6">{selectedTask.description || 'Không có mô tả chi tiết.'}</p>

                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Sản phẩm nhóm đã nộp</h4>
                  {submissions.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      Chưa có nhóm nào nộp sản phẩm.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {submissions.map(sub => (
                        <div key={sub.id} className="p-5 rounded-xl bg-gray-50/80 border border-gray-100 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-bold text-gray-900">Người nộp: {sub.submitted_by}</p>
                              <p className="text-xs text-gray-500 mt-1">Nộp lúc: {new Date(sub.submitted_at).toLocaleString('vi-VN')}</p>
                              {sub.teacher_score != null && <p className="text-xs font-bold text-emerald-700 mt-1">✓ GV đã chấm: {sub.teacher_score}/10</p>}
                            </div>
                            {sub.file_url && (
                              <a href={sub.file_url} target="_blank" rel="noreferrer"
                                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 shrink-0">
                                Xem sản phẩm <ExternalLink size={13} />
                              </a>
                            )}
                          </div>
                          {sub.content && <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-50 whitespace-pre-wrap">{sub.content}</p>}
                          <div className="pt-2 border-t border-gray-100">
                            <div className="text-[11px] font-bold text-gray-400 uppercase mb-1.5">GV chấm điểm nhóm (thang /10 — vào thống kê bài & khoá)</div>
                            <GroupGradeBox sub={sub} onGraded={(s) => setSubmissions((prev) => prev.map((x) => x.id === s.id ? s : x))} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-300 bg-white rounded-2xl border border-gray-100">
                  <FileText size={48} className="mb-3 text-gray-200" />
                  <p className="font-semibold text-gray-400">Chọn một nhiệm vụ học tập để quản lý bài nộp</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------------- STUDENT: MY GROUPS TAB ---------------- */}
        {!isTeacher && activeTab === 'my-group' && !loading && (
          <div className="max-w-2xl bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            {myGroups.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Users size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="font-semibold">Bạn chưa được phân vào nhóm nào</p>
                <p className="text-xs text-gray-400 mt-1">Liên hệ với giáo viên môn học để được phân vai và lập nhóm cộng tác.</p>
              </div>
            ) : (
              myGroups.map(g => (
                <div key={g.id}>
                  <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
                    <div>
                      <h2 className="text-2xl font-black text-gray-900">{g.group_name}</h2>
                      <p className="text-sm text-gray-500 mt-1">Lớp: {g.class_name}</p>
                    </div>
                    <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <UserCheck size={14} /> Thành viên tích cực
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Thành viên và Vai trò phân chia</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {g.members.map(m => (
                      <div
                        key={m.id}
                        className={`p-4 rounded-xl border flex items-center justify-between ${
                          m.student_email === user?.email
                            ? 'border-blue-200 bg-blue-50/20'
                            : 'border-gray-50 bg-gray-50/40'
                        }`}
                      >
                        <div>
                          <p className="font-bold text-gray-900 text-sm">{m.student_name || m.student_email}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{m.student_email}</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-black uppercase ${
                          m.role === 'Leader' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {m.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ---------------- STUDENT: TASKS & SUBMISSIONS TAB ---------------- */}
        {!isTeacher && activeTab === 'student-tasks' && !loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tasks list */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Nhiệm vụ được giao</h2>
              {tasks.length === 0 ? (
                <div className="p-6 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
                  Lớp của bạn chưa có nhiệm vụ nào.
                </div>
              ) : (
                tasks.map(t => (
                  <div
                    key={t.id}
                    onClick={() => handleSelectTask(t)}
                    className={`p-5 rounded-2xl border cursor-pointer transition ${
                      selectedTask?.id === t.id
                        ? 'border-blue-600 bg-blue-50/30'
                        : 'border-gray-100 bg-white hover:bg-gray-50/55'
                    }`}
                  >
                    <span className="bg-blue-50 text-blue-700 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">
                      Lớp {t.class_name}
                    </span>
                    <h4 className="font-bold text-gray-900 text-sm mt-2">{t.title}</h4>
                    <p className="text-xs text-gray-400 mt-2">
                      Hạn: {t.due_date ? new Date(t.due_date).toLocaleDateString('vi-VN') : 'Không hạn'}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Submission Form and Status */}
            <div className="lg:col-span-2">
              {selectedTask ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <h3 className="text-lg font-black text-gray-900 mb-2">{selectedTask.title}</h3>
                  <p className="text-sm text-gray-500 mb-6">{selectedTask.description || 'Không có mô tả chi tiết.'}</p>

                  {myGroups.length === 0 ? (
                    <div className="p-4 bg-amber-50 text-amber-800 rounded-xl border border-amber-100 text-sm">
                      Bạn phải có nhóm học tập mới có thể thực hiện nộp bài.
                    </div>
                  ) : (() => {
                    const myGroup = myGroups[0]
                    const mySub = submissions.find(s => s.group_id === myGroup.id)
                    const avgScore = mySubmissionReviews.length > 0
                      ? (mySubmissionReviews.reduce((sum, r) => sum + r.score, 0) / mySubmissionReviews.length).toFixed(1)
                      : null

                    return (
                      <div className="space-y-6">
                        {mySub && !showEditForm ? (
                          <div className="p-5 rounded-2xl bg-emerald-50/30 border border-emerald-100 space-y-4">
                            <div className="flex items-center justify-between border-b border-emerald-100 pb-3">
                              <div>
                                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">Trạng thái bài nộp</span>
                                <span className="text-sm font-bold text-emerald-800">Đã nộp thành công ✓</span>
                              </div>
                              <div className="flex items-center gap-4">
                                {mySub.teacher_score != null && (
                                  <div className="text-right">
                                    <span className="text-xs font-bold text-emerald-700 block">Điểm giáo viên</span>
                                    <span className="inline-flex items-center gap-1 text-sm font-black text-emerald-800">
                                      <Star size={14} className="fill-emerald-500 text-emerald-500" /> {mySub.teacher_score}/10
                                    </span>
                                  </div>
                                )}
                                {avgScore && (
                                  <div className="text-right">
                                    <span className="text-xs font-bold text-amber-700 block">TB nhóm khác chấm</span>
                                    <span className="inline-flex items-center gap-1 text-sm font-black text-amber-800">
                                      <Star size={14} className="fill-amber-500 text-amber-500" /> {avgScore}/10
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-500 uppercase">Mô tả sản phẩm</p>
                              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{mySub.content || '—'}</p>
                            </div>
                            {mySub.teacher_feedback && (
                              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                                <p className="text-xs font-bold text-emerald-700 uppercase flex items-center gap-1"><MessageSquare size={13} /> Nhận xét của giáo viên</p>
                                <p className="text-sm text-emerald-900 mt-1 whitespace-pre-wrap">{mySub.teacher_feedback}</p>
                              </div>
                            )}
                            {mySub.file_url && (
                              <div>
                                <p className="text-xs font-bold text-gray-500 uppercase">Tài liệu đính kèm</p>
                                <a
                                  href={mySub.file_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-700"
                                >
                                  Mở liên kết sản phẩm <ExternalLink size={14} />
                                </a>
                              </div>
                            )}
                            <div className="flex gap-2 justify-between items-center text-xs text-gray-400 pt-2 border-t border-gray-100">
                              <span>Nộp bởi: {mySub.submitted_by}</span>
                              <button
                                onClick={() => setShowEditForm(true)}
                                className="text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1"
                              >
                                <Edit2 size={13} /> Cập nhật bài nộp
                              </button>
                            </div>

                            {/* Peer Feedback List */}
                            <div className="mt-6 pt-4 border-t border-gray-100">
                              <h4 className="text-xs font-black text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <MessageSquare size={14} />
                                Nhận xét và góp ý từ các nhóm khác ({mySubmissionReviews.length})
                              </h4>
                              {mySubmissionReviews.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">Chưa có đánh giá chéo nào cho sản phẩm của nhóm bạn.</p>
                              ) : (
                                <div className="space-y-3">
                                  {mySubmissionReviews.map(r => (
                                    <div key={r.id} className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                                      <div className="flex justify-between items-center text-xs font-bold mb-1">
                                        <span className="text-gray-600">Thành viên lớp ẩn danh</span>
                                        <span className="text-amber-600 flex items-center gap-0.5">
                                          <Star size={12} className="fill-amber-500 text-amber-500" /> {r.score}/10
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-600">{r.feedback || 'Không có bình luận chi tiết.'}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="text-sm font-bold text-gray-800">
                                {mySub ? 'Cập nhật sản phẩm nhóm' : 'Nộp sản phẩm mới'}
                              </h4>
                              {mySub && (
                                <button
                                  type="button"
                                  onClick={() => setShowEditForm(false)}
                                  className="text-xs text-gray-400 hover:text-gray-600"
                                >
                                  Quay lại bài nộp cũ
                                </button>
                              )}
                            </div>
                            <form onSubmit={(e) => handleSubmitProduct(e, myGroup.id)} className="space-y-4">
                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Nội dung sản phẩm nhóm (báo cáo, mô tả)</label>
                                <textarea
                                  value={submissionContent}
                                  onChange={(e) => setSubmissionContent(e.target.value)}
                                  placeholder="Mô tả tóm tắt quá trình làm việc và sản phẩm của nhóm..."
                                  rows={5}
                                  className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Link sản phẩm — video (YouTube), Canva, Drive, Slide, PDF…</label>
                                <input
                                  type="url"
                                  value={submissionUrl}
                                  onChange={(e) => setSubmissionUrl(e.target.value)}
                                  placeholder="https://youtu.be/…  ·  canva.com/…  ·  drive.google.com/…"
                                  className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 bg-white"
                                />
                                <div className="flex items-center gap-2 mt-2">
                                  <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 text-xs font-bold hover:bg-sky-100 cursor-pointer">
                                    {uploadingSub ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Hoặc tải file (ảnh/PDF/Word/PPT)
                                    <input
                                      type="file" className="hidden" accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx"
                                      disabled={uploadingSub}
                                      onChange={async (e) => {
                                        const f = e.target.files?.[0]; e.currentTarget.value = ''
                                        if (!f) return
                                        setUploadingSub(true)
                                        try { const r = await collaborationService.uploadFile(f); setSubmissionUrl(r.url) }
                                        catch (err: any) { alert('Tải file chưa được: ' + (err?.message || 'thử lại') + '\n(Video/tệp lớn hãy dán LINK YouTube/Canva/Drive.)') }
                                        finally { setUploadingSub(false) }
                                      }}
                                    />
                                  </label>
                                  {submissionUrl && <span className="text-[11px] text-emerald-600 font-semibold truncate">Đã gắn: {submissionUrl.split('/').pop()}</span>}
                                </div>
                              </div>
                              <button
                                type="submit"
                                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-md transition flex items-center justify-center gap-1.5"
                              >
                                <Send size={16} /> Gửi sản phẩm nhóm
                              </button>
                            </form>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-300 bg-white rounded-2xl border border-gray-100">
                  <FileText size={48} className="mb-3 text-gray-200" />
                  <p className="font-semibold text-gray-400">Chọn nhiệm vụ bên trái để xem nội dung và nộp bài</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------------- STUDENT: PEER REVIEW (nhóm ĐƯỢC PHÂN) ---------------- */}
        {!isTeacher && activeTab === 'student-tasks' && selectedTask && (
          <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-black text-gray-900 mb-1">Đánh giá chéo — nhóm bạn được phân</h3>
            <p className="text-xs text-gray-400 mb-4">Mỗi nhóm chấm 1 nhóm khác theo vòng tròn; bạn không chấm nhóm mình.</p>
            {reviewTargetSub ? (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-bold text-gray-800">Nhóm cần đánh giá</p>
                  <p className="text-xs text-gray-500 mt-0.5">Nộp lúc: {new Date(reviewTargetSub.submitted_at).toLocaleDateString('vi-VN')}</p>
                  {reviewTargetSub.file_url && (
                    <a href={reviewTargetSub.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-blue-600 hover:text-blue-700">
                      Xem sản phẩm <ExternalLink size={12} />
                    </a>
                  )}
                </div>
                <button
                  onClick={() => handleOpenReviewModal(reviewTargetSub)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition"
                >
                  Bắt đầu đánh giá
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Chưa đủ nhóm nộp để phân đánh giá chéo (cần ít nhất 2 nhóm nộp sản phẩm).</p>
            )}
          </div>
        )}
      </main>

      {/* ---------------- MODALS / FORMS ---------------- */}

      {/* Group Create/Edit Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl p-6 shadow-xl relative animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-black text-gray-900 mb-4">
              {editingGroup ? 'Chỉnh sửa nhóm cộng tác' : 'Tạo nhóm cộng tác mới'}
            </h3>
            <form onSubmit={handleSaveGroup} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Tên nhóm</label>
                  <input
                    type="text"
                    required
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Nhóm 1, Nhóm A..."
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Lớp</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingGroup}
                    value={className}
                    onChange={(e) => setClassName(e.target.value)}
                    placeholder="10A1, 11 lý..."
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white disabled:opacity-50"
                  />
                </div>
              </div>

              {/* #1: Chọn nhanh thành viên từ danh sách lớp */}
              <div>
                <button type="button" onClick={loadRoster} disabled={rosterLoading}
                  className="text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 disabled:opacity-50">
                  {rosterLoading ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
                  Chọn từ danh sách lớp{className ? ` "${className}"` : ' (nhập Lớp trước)'}
                </button>
                {roster.length > 0 && (
                  <div className="mt-1.5 max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2 space-y-1 bg-gray-50/50">
                    {roster.map((r) => {
                      const checked = groupMembers.some((m) => m.student_email === r.student_email)
                      return (
                        <label key={r.student_email} className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" checked={checked} onChange={() => toggleRosterMember(r)} />
                          <span className="font-semibold text-gray-800">{r.student_name || r.student_email}</span>
                          <span className="text-gray-400">· {r.student_email}</span>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Members input list */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Thành viên và phân vai (tick từ lớp ở trên hoặc nhập tay)</label>
                <div className="space-y-2 max-h-56 overflow-y-auto p-1">
                  {groupMembers.map((m, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="email"
                        required
                        placeholder="Email học sinh..."
                        value={m.student_email}
                        onChange={(e) => {
                          const list = [...groupMembers]
                          list[idx].student_email = e.target.value
                          setGroupMembers(list)
                        }}
                        className="flex-1 p-2 border border-gray-200 rounded-lg text-xs"
                      />
                      <input
                        type="text"
                        placeholder="Tên..."
                        value={m.student_name}
                        onChange={(e) => {
                          const list = [...groupMembers]
                          list[idx].student_name = e.target.value
                          setGroupMembers(list)
                        }}
                        className="w-28 p-2 border border-gray-200 rounded-lg text-xs"
                      />
                      <select
                        value={m.role}
                        onChange={(e) => {
                          const list = [...groupMembers]
                          list[idx].role = e.target.value
                          setGroupMembers(list)
                        }}
                        className="w-24 p-2 border border-gray-200 rounded-lg text-xs bg-white"
                      >
                        <option value="Leader">Nhóm trưởng</option>
                        <option value="Researcher">Nghiên cứu</option>
                        <option value="Writer">Thư ký</option>
                        <option value="Presenter">Thuyết trình</option>
                        <option value="Member">Thành viên</option>
                      </select>
                      {groupMembers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setGroupMembers(groupMembers.filter((_, i) => i !== idx))}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setGroupMembers([...groupMembers, { student_email: '', student_name: '', role: 'Member' }])}
                  className="mt-2 text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus size={14} /> Thêm học sinh
                </button>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowGroupModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl"
                >
                  Lưu thông tin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Create Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-xl relative animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-black text-gray-900 mb-4">{editingTask ? 'Sửa nhiệm vụ nhóm' : 'Giao nhiệm vụ nhóm mới'}</h3>
            <form onSubmit={handleSaveTask} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Tiêu đề nhiệm vụ</label>
                <input
                  type="text"
                  required
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="Báo cáo thí nghiệm ảo, Thiết kế cầu..."
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Chi tiết yêu cầu</label>
                <textarea
                  value={taskDesc}
                  onChange={(e) => setTaskDesc(e.target.value)}
                  placeholder="Mô tả chi tiết và các tiêu chí đánh giá sản phẩm..."
                  rows={4}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Lớp giao</label>
                  <input
                    type="text"
                    required
                    value={taskClass}
                    onChange={(e) => setTaskClass(e.target.value)}
                    placeholder="10A1, 11 Lý..."
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Hạn nộp</label>
                  <input
                    type="datetime-local"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Gắn vào bài học (để điểm nhóm vào thống kê khoá)</label>
                <select value={taskLesson} onChange={(e) => setTaskLesson(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white">
                  <option value="">— Không gắn bài (chỉ đánh giá chéo) —</option>
                  {lessons.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => { setShowTaskModal(false); setEditingTask(null) }}
                  className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl"
                >
                  {editingTask ? 'Cập nhật' : 'Giao bài'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && reviewSubmission && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 shadow-xl relative animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-black text-gray-900 mb-1">Đánh giá & Bình luận</h3>
            <p className="text-xs text-gray-400 mb-4">Chấm chéo sản phẩm của nhóm bạn nộp lúc: {new Date(reviewSubmission.submitted_at).toLocaleString('vi-VN')}</p>

            <form onSubmit={handleSaveReview} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Cho điểm (Thang 1 - 10)</label>
                <div className="flex gap-1.5 justify-between">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewScore(star)}
                      className={`w-9 h-9 rounded-full font-bold text-xs flex items-center justify-center transition-all ${
                        reviewScore >= star
                          ? 'bg-amber-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {star}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Ý kiến bình luận, góp ý</label>
                <textarea
                  required
                  value={reviewFeedback}
                  onChange={(e) => setReviewFeedback(e.target.value)}
                  placeholder="Nêu nhận xét chi tiết, góp ý xây dựng để nhóm bạn hoàn thiện hơn..."
                  rows={4}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white"
                />
              </div>

              {/* View current reviews */}
              {currentReviews.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Nhận xét đã có</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                    {currentReviews.map(r => (
                      <div key={r.id} className="text-xs border-b border-gray-100 pb-1.5 last:border-b-0">
                        <div className="flex justify-between items-center font-bold text-gray-700">
                          <span>{r.reviewer_email}</span>
                          <span className="text-amber-600 flex items-center gap-0.5"><Star size={11} className="fill-amber-600" /> {r.score}/10</span>
                        </div>
                        <p className="text-gray-500 mt-0.5">{r.feedback}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-50"
                >
                  Đóng
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl"
                >
                  Gửi đánh giá
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CollaborationPage
